import { useReactiveVar } from '@apollo/client';

import { useMyCareerVacancyQuery } from 'core/graphql';
import { authState } from 'core/state';
import { useCareerCapabilitySkip } from 'core/hooks/CareerJourney/useCareerCapabilitySkip';
import { isCareerViewer } from './isCareerViewer';

/**
 * One vacancy for the details drawer (spec 0035).
 *
 * 🔴 Fixes a live bug. The drawer used to pick its row out of the loaded list
 * (`vacancies.find(...)`) on a `cache-and-network` query — so a background
 * refetch or a filter change made the row disappear and the drawer close
 * **in the middle of reading**. Under pagination it would be worse: the row
 * may not be on the loaded page at all. And it is now the only place the FULL
 * description exists, since the list ships `descriptionShort`.
 *
 * `cache-first`: the entity is already normalized by the feed, so the drawer
 * paints instantly with everything except the description and fills that in.
 * The backend applies the same PUBLISHED + unexpired filter as the feed, so a
 * guessed id cannot reach a draft.
 */
export const useCareerVacancy = (id: string | null) => {
  const { active_role, isDemoUser } = useReactiveVar(authState);
  // ТЗ 0069: не-участник больше не выбрасывается со страницы, он остаётся на
  // ней и читает объяснение. Без этого пропуска сервер отбил бы запрос, и
  // экран показал бы «ошибка загрузки» вместо «раздел откроется участникам».
  const lockedSkip = useCareerCapabilitySkip('vacancies');
  const skip = !id || !isCareerViewer(active_role) || Boolean(isDemoUser) || lockedSkip;

  const { data, loading, error } = useMyCareerVacancyQuery({
    variables: { id: id as string },
    fetchPolicy: 'cache-first',
    skip,
  });

  return {
    vacancy: data?.careerVacancy ?? null,
    loading,
    error: error?.message || '',
  };
};
